import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FamilyIcon, SearchInput, DifficultyBars, useEscape } from '../../components/ui';
import { CATALOG_TYPES, FAMILIES, FAMILY_ORDER, setById } from '../../config/assessmentFamilies';
import { examSetAPI } from '../../services/api';
import { examModeAllowed } from '../../utils/entitlements';
import { codingLanguageLabel } from '../../utils/codingLanguages';
import { SKILL_AREA_LABELS } from '../../components/campaign/GroupedQuestionList';
import PermissionDeniedPanel from '../../components/PermissionDeniedPanel';
import { setCount } from './questionSets';

const STEPS = ['Choose a type', 'Set up', 'Add candidates'];

const typeLabel = (t) => {
  const v = String(t || 'technical').toLowerCase();
  return v.charAt(0).toUpperCase() + v.slice(1);
};

/** Text questions an interview type can use: JD rows for AI types, every proficiency row for language exams. */
function poolForType(type, questions) {
  if (!type.sets.some((id) => !setById(id)?.coding)) return [];
  const category = type.examType === 'english' ? 'english' : 'jd';
  return (questions || []).filter((q) => q.id && (q.exam_category || 'jd') === category);
}

function EmptySet({ set, onGo }) {
  return (
    <div className="wz-setup-empty">
      <p style={{ fontWeight: 500 }}>No {set.name.toLowerCase()} on this position yet</p>
      <p className="wz-sub">Generate or write them in the Question bank. You’ll come straight back here once they’re saved.</p>
      <Button variant="primary" icon="book" onClick={onGo}>Go to Question bank</Button>
    </div>
  );
}

/**
 * New interview: 1) choose a type, 2) set up — three settings and the position's
 * existing questions to pick from, 3) hand over to Add candidates (onCreated).
 * Questions are only generated in the Question bank; when the type has none,
 * `onNeedQuestions(typeId, setId)` sends the recruiter there and back.
 */
export default function NewInterviewDialog({ campaign, data, initialTypeId, onClose, onCreated, onNeedQuestions, onAddBatch }) {
  const boxRef = useRef(null);
  useEscape(onClose, { ref: boxRef });
  const [cat, setCat] = useState('all');
  const [search, setSearch] = useState('');
  const [selId, setSelId] = useState(initialTypeId || null);
  const [step, setStep] = useState(initialTypeId ? 2 : 1);
  const [denied, setDenied] = useState(false);
  useEffect(() => { boxRef.current?.focus(); }, []);

  const sel = CATALOG_TYPES.find((t) => t.id === selId);
  const inUse = useMemo(() => new Set(data.examSets.map((es) => es.exam_type)), [data.examSets]);
  // One interview per type per position: picking a type that is already here runs
  // it again as the next batch of that interview (Batch 2, Batch 3...).
  const existingOf = (t) => data.examSets.find((es) => es.exam_type === t.examType);
  const nextBatchName = (es) => `Batch ${((data.batchesByExamSet || {})[es.id] || []).length + 1}`;
  const selExisting = sel ? existingOf(sel) : null;
  const q = search.trim().toLowerCase();
  const list = CATALOG_TYPES.filter((t) => (cat === 'all' || t.fam === cat) && (!q || `${t.name} ${t.desc}`.toLowerCase().includes(q)));
  const cats = [['all', 'All types'], ...FAMILY_ORDER.map((f) => [f, FAMILIES[f].label])];

  /* ---------- step 2 state ---------- */
  const pool = useMemo(() => (sel ? poolForType(sel, data.questions) : []), [sel, data.questions]);
  const usesText = !!sel && sel.sets.some((id) => !setById(id)?.coding);
  const usesCoding = !!sel && sel.sets.some((id) => setById(id)?.coding);
  const textSet = sel ? setById(sel.sets.find((id) => !setById(id)?.coding)) : null;
  const codingSet = setById('code');
  const codingCount = data.codingQuestions.length;

  const [picked, setPicked] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Entering setup (or the pool changing after a trip to the Question bank) selects every question.
  useEffect(() => {
    if (step === 2) setPicked(new Set(pool.map((x) => x.id)));
  }, [step, pool]);

  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPicked = pool.length > 0 && pool.every((x) => picked.has(x.id));

  const continueToSetup = () => {
    if (!sel) return;
    if (selExisting) { onAddBatch?.(selExisting.id); return; }
    if (!examModeAllowed(sel.examType)) { setDenied(true); return; }
    setDenied(false);
    setError('');
    setStep(2);
  };

  const goToBank = (set) => onNeedQuestions?.(sel.id, set.id);

  const handleCreate = async () => {
    if (usesText && !picked.size) { setError(pool.length ? 'Select at least one question.' : `Add ${textSet.name.toLowerCase()} first.`); return; }
    if (usesCoding && !codingCount) { setError('Add at least one coding problem first.'); return; }
    setCreating(true);
    setError('');
    try {
      // Keep the Question bank order for the picked questions.
      const questionIds = pool.filter((x) => picked.has(x.id)).map((x) => x.id);
      const res = await examSetAPI.create(campaign.id, {
        name: `${sel.legacyTitle || sel.name} ${data.examSets.length + 1}`,
        exam_type: sel.examType,
        question_ids: questionIds,
      });
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create the interview.');
      setCreating(false);
    }
  };

  /* ---------- step 2 body ---------- */
  const setupBody = sel && (
    <div className="wz-dialog-main" style={{ flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FamilyIcon fam={sel.fam} size="m" />
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{sel.name}</div>
          <div className="wz-sub">{sel.desc}</div>
        </div>
      </div>

      <p className="wz-sub">Grading strictness and hire score are set per batch, when you add candidates.</p>

      {error && <div className="wz-error" role="alert">{error}</div>}

      {usesText && (
        <section className="wz-setup-list">
          <div className="wz-setup-list-h">
            <span><b>{textSet.name}</b> <span className="wz-sub">· {picked.size} of {pool.length} selected</span></span>
            {pool.length > 0 && (
              <Button small variant="ghost" onClick={() => setPicked(allPicked ? new Set() : new Set(pool.map((x) => x.id)))}>
                {allPicked ? 'Clear all' : 'Select all'}
              </Button>
            )}
          </div>
          {pool.length ? (
            <div className="wz-setup-rows">
              {pool.map((row, i) => (
                <label key={row.id} className="wz-setup-row">
                  <input type="checkbox" checked={picked.has(row.id)} onChange={() => toggle(row.id)} />
                  <span className="wz-sub">{i + 1}</span>
                  <span style={{ lineHeight: 1.45 }}>{row.question}</span>
                  <span className="wz-sub" style={{ whiteSpace: 'nowrap' }}>
                    {sel.examType === 'english' ? (SKILL_AREA_LABELS[row.skill_area] || 'Proficiency') : typeLabel(row.type)}
                  </span>
                  <DifficultyBars level={row.difficulty} />
                </label>
              ))}
            </div>
          ) : <EmptySet set={textSet} onGo={() => goToBank(textSet)} />}
        </section>
      )}

      {usesCoding && (
        <section className="wz-setup-list">
          <div className="wz-setup-list-h">
            <span><b>{codingSet.name}</b> <span className="wz-sub">· all {codingCount} included</span></span>
          </div>
          {codingCount ? (
            <div className="wz-setup-rows">
              {data.codingQuestions.map((row, i) => (
                <div key={row.id} className="wz-setup-row code">
                  <span className="wz-sub">{i + 1}</span>
                  <span style={{ fontWeight: 500 }}>{row.title}</span>
                  <span className="wz-sub">{(row.languages?.length ? row.languages : ['python']).map(codingLanguageLabel).join(', ')}</span>
                </div>
              ))}
            </div>
          ) : <EmptySet set={codingSet} onGo={() => goToBank(codingSet)} />}
        </section>
      )}
    </div>
  );

  return (
    <>
      <div className="wz-scrim" onClick={onClose} />
      <div className="wz-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div ref={boxRef} tabIndex={-1} className="wz-dialog-box" role="dialog" aria-modal="true" aria-label="New interview">
          <div className="wz-dialog-head">
            <div>
              <h2 style={{ fontSize: 20 }}>New interview</h2>
              <ol className="wz-steps">
                {STEPS.map((l, i) => (
                  <li key={l} className={i + 1 === step ? 'on' : i + 1 < step ? 'done' : ''}><b>{i + 1}</b>{l}</li>
                ))}
              </ol>
            </div>
            <Button iconOnly icon="x" aria-label="Close" onClick={onClose} />
          </div>

          {step === 1 ? (
            <div className="wz-dialog-body">
              <div className="wz-dialog-cats">
                {cats.map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={cat === k} onClick={() => setCat(k)}>
                    <span>{l}</span>
                    <span className="wz-sub" style={{ fontSize: 12 }}>{CATALOG_TYPES.filter((t) => k === 'all' || t.fam === k).length}</span>
                  </button>
                ))}
              </div>
              <div className="wz-dialog-main">
                <SearchInput label="Search interview types" placeholder="Search interview types" value={search} onChange={setSearch} width="100%" height={38} />
                <div className="wz-types">
                  {list.length ? list.map((t) => {
                    const set = setById(t.sets[0]);
                    const n = set ? setCount(set, data.questions, data.codingQuestions) : 0;
                    const warn = set && !n;
                    return (
                      <button key={t.id} type="button" className="wz-type" aria-pressed={selId === t.id} disabled={!!t.soon} onClick={() => { setSelId(t.id); setDenied(false); }}>
                        <span className="top">
                          <FamilyIcon fam={t.fam} size="m" />
                          <span style={{ fontSize: 12, fontWeight: 500, color: t.soon ? 'var(--wz-muted)' : 'var(--wz-pass)' }}>
                            {t.soon ? 'Coming soon' : (inUse.has(t.examType) ? `In use on this position · adds ${nextBatchName(existingOf(t))}` : '')}
                          </span>
                        </span>
                        <span style={{ fontSize: 15, fontWeight: 600 }}>{t.name}</span>
                        <span className="wz-sub" style={{ lineHeight: 1.45 }}>{t.desc}</span>
                        <span className={`wz-type-uses uses${warn ? ' warn' : ''}`}>
                          {t.soon ? 'Not available yet' : warn ? `${set.name} set is empty` : `Uses ${set.name} set (${n})`}
                        </span>
                      </button>
                    );
                  }) : <p className="wz-sub">No types match.</p>}
                  {denied ? <PermissionDeniedPanel /> : null}
                </div>
              </div>
            </div>
          ) : setupBody}

          <div className="wz-dialog-foot">
            <span className="wz-sub">
              {!sel ? 'Choose a type to continue'
                : selExisting ? `Selected: ${sel.name} — runs as ${nextBatchName(selExisting)} of ${selExisting.name}`
                  : `Selected: ${sel.name}`}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {step === 2 && <Button onClick={() => { setStep(1); setError(''); }}>Back</Button>}
              <Button onClick={onClose}>Cancel</Button>
              {step === 1 ? (
                <Button variant="primary" disabled={!sel} onClick={continueToSetup}>{selExisting ? 'Add a new batch' : 'Continue to setup'}</Button>
              ) : (
                <Button variant="primary" disabled={creating} onClick={handleCreate}>{creating ? 'Creating…' : 'Create interview'}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
